import { Audio } from 'types/audio';
import {
  deleteSongAudioEffect,
  downloadAudioEffect,
  removeSongLatestAudioVersionEffect,
  retryAudioEffect,
  setSongLatestAudioVersionEffect,
  updateSongAudioEffect,
} from 'app/store/SongList/effects';
import { AppDispatch } from 'app/store';
import { AudioDialogVariant } from 'app/components/Song/Audio/AudioTableList/types';
import { isLocalAudioGuid } from 'app/store/SongList/slice.utils';
import { deleteLocalSongAudioAction } from 'app/store/SongList/slice';
import { addAlertAction } from 'app/store/Alerts/slice';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { CONTROLS_SIZE, OPTIONS_SIZE, PRIVATE_SIZE } from './constants';

const getColumnSize = (percent: number, totalWidth?: number) => {
  if (!totalWidth) {
    return undefined;
  }
  return (totalWidth - CONTROLS_SIZE - OPTIONS_SIZE - PRIVATE_SIZE) * percent;
};

export const useAudioListColumns = (ref: React.RefObject<HTMLDivElement>) => {
  const totalWidth = useTotalWidth(ref);
  return [
    {
      accessorKey: 'controls',
      header: '',
      accessory: true,
      size: CONTROLS_SIZE,
    },
    {
      accessorKey: 'title',
      header: 'Title',
      sx: { pl: 0 },
      enableResizing: true,
      size: getColumnSize(0.25, totalWidth),
    },
    {
      accessorKey: 'date',
      header: 'Date',
      enableResizing: true,
      size: getColumnSize(0.15, totalWidth),
    },
    {
      accessorKey: 'user',
      header: 'User',
      enableResizing: true,
      size: getColumnSize(0.15, totalWidth),
    },
    {
      accessorKey: 'size',
      header: 'Size',
      minSize: 88,
      size: getColumnSize(0.15, totalWidth),
      enableResizing: true,
    },
    {
      accessorKey: 'duration',
      header: 'Duration',
      minSize: 84,
      size: getColumnSize(0.15, totalWidth),
      enableResizing: true,
    },
    {
      accessorKey: 'notes',
      header: 'Notes',
      enableResizing: true,
      size: getColumnSize(0.15, totalWidth),
      minSize: 64,
      centered: true,
    },
    {
      accessorKey: 'private',
      header: 'Private',
      size: PRIVATE_SIZE,
      centered: true,
    },
    {
      accessorKey: 'gap',
      size: 0,
    },
    {
      accessorKey: 'options',
      size: OPTIONS_SIZE,
      header: '',
      contextMenu: true,
    },
  ];
};

interface UseAudioListActionsProps {
  dispatch: AppDispatch;
  setDialogOpen: (props: AudioDialogVariant) => void;
}

export const useAudioListActions = ({
  dispatch,
  setDialogOpen,
}: UseAudioListActionsProps) => {
  const togglePrivateAudio = async (audio: Audio) => {
    if (audio.isLatestVersion) {
      setDialogOpen({ type: 'privateLatestVersion', audio });
    } else {
      dispatch(
        updateSongAudioEffect({
          audio,
          formData: {
            isPrivate: !audio.isPrivate,
          },
          togglingPrivate: true,
        })
      );
    }
  };

  const handleDelete = (audio: Audio) => async () => {
    try {
      if (isLocalAudioGuid(audio.guid)) {
        dispatch(deleteLocalSongAudioAction(audio));
        dispatch(addAlertAction({ message: `"${audio.title}" deleted` }));
      } else {
        await dispatch(deleteSongAudioEffect({ audio })).unwrap();
      }
    } catch (e) {
      dispatch(addAlertAction({ message: "Couldn't delete the audio." }));
    }
  };

  const handleSetLatestAudioVersion = (audio: Audio) =>
    dispatch(
      setSongLatestAudioVersionEffect({ audioGuid: audio.guid })
    ).unwrap();

  const handleDownload = (audio: Audio) =>
    dispatch(downloadAudioEffect({ guid: audio.guid }));

  const handleRemoveLatestAudioVersion = (audio: Audio) =>
    dispatch(
      removeSongLatestAudioVersionEffect({ audioGuid: audio.guid })
    ).unwrap();

  const handleRetry = (audio: Audio, songGuid?: string) => () => {
    dispatch(retryAudioEffect({ audio, songGuid }));
  };

  const addToPlaylist = (audio: Audio) =>
    setDialogOpen({ type: 'playlist', audio });

  return {
    togglePrivateAudio,
    handleSetLatestAudioVersion,
    handleDownload,
    handleRemoveLatestAudioVersion,
    handleRetry,
    handleDelete,
    addToPlaylist,
  };
};
